import React from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { NativeResources } from '@ariksa/inventory-core';
import { AlertResponse } from '@ariksa/notification';
import {
  Box,
  Flex,
  Stack,
  Text,
  useDisclosure,
  WrapItem,
} from '@chakra-ui/react';
import map from 'lodash/map';
import { colorOpacity, customTheme } from 'theme';

import {
  CustomTooltip,
  formatTooltip,
  StackedHeader,
  Tag,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { IconTypes } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { useMetadataDrawerTabs } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/hooks/useMetadataDrawerTabs';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { MetadataTabs } from 'containers/ActiveCloudResource/types';
import { getPortName } from 'containers/Findings/AlertDrawer/Components/Aggregated/utils';

interface Props {
  data: any;
  isLoading: boolean;
  alert: AlertResponse;
}

export const VMOpenPorts = (props: Props) => {
  const { data, isLoading, alert } = props;
  const portsDisclosure = useDisclosure();
  const [currentPortType, setCurrentPortType] = React.useState<
    Record<string, any>
  >({});
  const { updateActiveResource } = useActiveResourceActions();
  const { onOpenMetadata } = useActiveResourceContext();
  const { getTabIndex } = useMetadataDrawerTabs();

  const columns = [
    { header: 'CIDR', accessor: 'exposure.cidr', render: ({ value }) => value },
    { header: 'Port', accessor: 'port', render: ({ value }) => value },
    { header: 'Protocol', accessor: 'protocol', render: ({ value }) => value },
  ];

  const renderPort = (field, value) => {
    return (
      <WrapItem
        key={field}
        onClick={() => {
          portsDisclosure.onOpen();
          setCurrentPortType({ field, value });
        }}
        cursor={'pointer'}
      >
        <CustomTooltip
          label={formatTooltip({
            header: getPortName(field) + ' Ports',
            content: (
              <Text noOfLines={3}>{map(value, o => o.port)?.join(', ')}</Text>
            ),
          })}
        >
          <Tag
            label={value?.length + ' ' + getPortName(field)?.toLowerCase()}
            styles={{
              label: {
                color: customTheme.colors.critical,
              },
              tag: {
                bg: colorOpacity(customTheme.colors.critical, 0.1),
                border: 'none',
                py: 1,
                display: 'inline-flex',
                alignItems: 'center',
              },
            }}
          />
        </CustomTooltip>
      </WrapItem>
    );
  };
  return isLoading ? (
    <Flex align="flex-start">
      <CustomSpinner />
    </Flex>
  ) : (
    <>
      {!!data?.access_ports?.length &&
        renderPort('access_ports', data?.access_ports)}
      {!!data?.db_ports?.length && renderPort('db_ports', data?.db_ports)}
      {!!data?.forbidden_ports?.length &&
        renderPort('forbidden_ports', data?.forbidden_ports)}
      {!!data?.non_standard_ports?.length &&
        renderPort('non_standard_ports', data?.non_standard_ports)}
      {!!data?.web_app_ports?.length &&
        renderPort('web_app_ports', data?.web_app_ports)}
      {portsDisclosure.isOpen && (
        <Modal
          isOpen={portsDisclosure.isOpen}
          onClose={portsDisclosure.onClose}
          header={
            <StackedHeader
              type={IconTypes.Port}
              upper={alert?.entity_name ?? alert?.entity_id}
              lower={getPortName(currentPortType?.field) + ' Ports'}
            />
          }
          body={
            <Stack spacing={6}>
              <Box h="300px">
                <Table data={currentPortType?.value} columns={columns} />
              </Box>
              <Text
                color="primary"
                cursor="pointer"
                _hover={{ textDecoration: 'underline' }}
                onClick={() => {
                  updateActiveResource({
                    uuid: alert.entity_uuid!,
                    resourceId: alert.entity_id!,
                    resourceType: alert.resource as NativeResources,
                    accountId: alert.account_id ?? '',
                    cloudProvider: alert?.cloud?.toLowerCase() as CloudProviders,
                  });
                  onOpenMetadata(
                    getTabIndex(alert.resource, MetadataTabs.Network),
                  );
                }}
              >
                Click to view all ingress traffic rules
              </Text>
            </Stack>
          }
          size={'xl'}
        />
      )}
    </>
  );
};
